import React, {useMemo, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {ViewMode} from '@taopix/taopix-core';
import {
  Button,
  GridSizeSmallIcon,
  GridSizeLargeIcon,
  Theme,
  ThemeName,
  MatchesMediaQuery,
  Icon,
  GridSizeMediumIcon,
} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import classNames from 'classnames';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useExperience} from '../Experience/ExperienceProvider';
import useViewMode from '../../Hooks/useViewMode';

export enum GridSizes {
  XXSMALL = 120,
  XSMALL = 135,
  SMALL = 205,
  MEDIUM = 300,
  LARGE = 360,
}

const GridSizeSelector = () => {
  const ui = useUiState();
  const {t} = useTranslation();
  const {experience} = useExperience();
  const [viewMode] = useViewMode();
  // Screensize breakpoints for changing the grid size. These update dynamically because MatchesMediaQuery is a hook
  const bp0 = MatchesMediaQuery('(max-width:400px)');
  const bp1 = MatchesMediaQuery('(max-width:609px)');
  const bp2 = MatchesMediaQuery('(max-width:799px)');
  const bp3 = MatchesMediaQuery('(max-width:960px)');
  const bp4 = MatchesMediaQuery('(max-width:1100px)');
  const bp5 = MatchesMediaQuery('(max-width:1650px)');

  const autoGridSize = useMemo(() => {
    if (bp0) return GridSizes.XXSMALL;
    if (bp1) return GridSizes.XSMALL;
    if (bp2) return GridSizes.SMALL;
    if (bp3) return GridSizes.XSMALL;
    if (bp4) return GridSizes.SMALL;
    if (bp5) return GridSizes.MEDIUM;
    return GridSizes.LARGE;
  }, [bp0, bp1, bp2, bp3, bp4, bp5]);

  useEffect(() => {
    // When the screen size changes, and auto grid sizing is currently on (or we're on small screen), set the grid size to the appropriate size
    if (ui.state.autoGridSizeSwitching || ui.state.isSmallScreen)
      ui.dispatch(UserInterfaceActions.setGridSize(autoGridSize));
  }, [autoGridSize, ui.state.autoGridSizeSwitching, ui.state.isSmallScreen]);

  const gridSizeToolbarClasses = classNames(
    'flex',
    'shadow-md',
    'rounded-themeCornerSize',
    'transition-[transform]',
    'ease-[cubic-bezier(0.42,0,0,2.27)]',
    [ViewMode.Grid].includes(viewMode)
      ? 'duration-[250ms] delay-[500ms]'
      : 'translate-x-[calc(100%+20rem)] duration-[1s]'
  );

  // Don't show the grid size selector if not in griod mode or it is not configured in the experience
  if (ViewMode.Grid !== viewMode || !experience.EDITOR.workArea.gridView.gridSizeControl) {
    return <></>;
  }

  return (
    <>
      {!ui.state.isSmallScreen && (
        <>
          <Theme name={ThemeName.Popouts} className={gridSizeToolbarClasses}>
            <Button
              label={t('str_LabelSmallGridTiles')}
              startIcon={
                <Icon
                  icon={<GridSizeSmallIcon />}
                  size={'small'}
                  colour={
                    ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.SMALL
                      ? 'themeAccentColour'
                      : undefined
                  }
                />
              }
              buttonStyle={'standard'}
              tabPosition={'left'}
              size={'small'}
              hideLabel
              aria-pressed={
                !ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.SMALL
              }
              onClick={() => {
                if (!ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.SMALL) {
                  ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(true));
                } else {
                  ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(false));
                  ui.dispatch(UserInterfaceActions.setGridSize(GridSizes.SMALL));
                }
              }}
              className={'rounded-l-themeCornerSize'}
              corners={'square'}
            />
            <Button
              label={t('str_LabelSmallGridTiles')}
              startIcon={
                <Icon
                  icon={<GridSizeMediumIcon />}
                  size={'small'}
                  colour={
                    ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.MEDIUM
                      ? 'themeAccentColour'
                      : undefined
                  }
                />
              }
              buttonStyle={'standard'}
              size={'small'}
              hideLabel
              aria-pressed={
                !ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.MEDIUM
              }
              onClick={() => {
                if (!ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.MEDIUM) {
                  ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(true));
                } else {
                  ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(false));
                  ui.dispatch(UserInterfaceActions.setGridSize(GridSizes.MEDIUM));
                }
              }}
              corners={'square'}
            />
            <Button
              label={t('str_LabelLargeGridTiles')}
              startIcon={
                <Icon
                  icon={<GridSizeLargeIcon />}
                  size={'small'}
                  colour={
                    ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.LARGE
                      ? 'themeAccentColour'
                      : undefined
                  }
                />
              }
              buttonStyle={'standard'}
              tabPosition={'right'}
              size={'small'}
              hideLabel
              aria-pressed={
                !ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.LARGE
              }
              onClick={() => {
                if (!ui.state.autoGridSizeSwitching && ui.state.gridSize === GridSizes.LARGE) {
                  ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(true));
                } else {
                  ui.dispatch(UserInterfaceActions.setGridSize(GridSizes.LARGE));
                  ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(false));
                }
              }}
              className={'rounded-r-themeCornerSize'}
              corners={'square'}
            />
          </Theme>
        </>
      )}
    </>
  );
};

export default GridSizeSelector;
